/**
 * Animated Chess Board Component for Landing Page
 * Shows a demo board with animated pieces
 */

import React, { useEffect, useState } from 'react';
import type { Square, Piece, Color } from '@jester-chess/shared';
import { PieceIcon } from './PieceIcon';
import './AnimatedBoard.css';

interface AnimatedBoardProps {
  size?: number;
}

const files: Array<'a' | 'b' | 'c' | 'd' | 'e' | 'f' | 'g' | 'h'> = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
const ranks: number[] = [8, 7, 6, 5, 4, 3, 2, 1];

export const AnimatedBoard: React.FC<AnimatedBoardProps> = ({ size = 400 }) => {
  const [highlightedSquare, setHighlightedSquare] = useState<Square | null>(null);
  const [animatedPiece, setAnimatedPiece] = useState<{ piece: Piece; from: Square; to: Square } | null>(null);
  const [diceRolling, setDiceRolling] = useState(false);
  const [diceValues, setDiceValues] = useState<{ file: number; rank: number }>({ file: 1, rank: 1 });
  const [showDice, setShowDice] = useState(true);
  const [gameWon, setGameWon] = useState(false);
  const [winMessage, setWinMessage] = useState<string>('');
  const [animationPhase, setAnimationPhase] = useState<0 | 1 | 2>(0);

  // Initial board setup with jesters
  const initialPieces: Piece[] = [
    // White pieces
    { type: 'rook', color: 'white', square: { file: 'a', rank: 1 } },
    { type: 'knight', color: 'white', square: { file: 'b', rank: 1 } },
    { type: 'bishop', color: 'white', square: { file: 'c', rank: 1 } },
    { type: 'queen', color: 'white', square: { file: 'd', rank: 1 } },
    { type: 'king', color: 'white', square: { file: 'e', rank: 1 } },
    { type: 'bishop', color: 'white', square: { file: 'f', rank: 1 } },
    { type: 'knight', color: 'white', square: { file: 'g', rank: 1 } },
    { type: 'rook', color: 'white', square: { file: 'h', rank: 1 } },
    { type: 'pawn', color: 'white', square: { file: 'a', rank: 2 } },
    { type: 'pawn', color: 'white', square: { file: 'b', rank: 2 } },
    { type: 'pawn', color: 'white', square: { file: 'c', rank: 2 } },
    { type: 'pawn', color: 'white', square: { file: 'd', rank: 2 } },
    { type: 'pawn', color: 'white', square: { file: 'e', rank: 2 } },
    { type: 'pawn', color: 'white', square: { file: 'f', rank: 2 } },
    { type: 'pawn', color: 'white', square: { file: 'g', rank: 2 } },
    { type: 'pawn', color: 'white', square: { file: 'h', rank: 2 } },
    // Black pieces
    { type: 'rook', color: 'black', square: { file: 'a', rank: 8 } },
    { type: 'knight', color: 'black', square: { file: 'b', rank: 8 } },
    { type: 'bishop', color: 'black', square: { file: 'c', rank: 8 } },
    { type: 'queen', color: 'black', square: { file: 'd', rank: 8 } },
    { type: 'king', color: 'black', square: { file: 'e', rank: 8 } },
    { type: 'bishop', color: 'black', square: { file: 'f', rank: 8 } },
    { type: 'knight', color: 'black', square: { file: 'g', rank: 8 } },
    { type: 'rook', color: 'black', square: { file: 'h', rank: 8 } },
    { type: 'pawn', color: 'black', square: { file: 'a', rank: 7 } },
    { type: 'pawn', color: 'black', square: { file: 'b', rank: 7 } },
    { type: 'pawn', color: 'black', square: { file: 'c', rank: 7 } },
    { type: 'pawn', color: 'black', square: { file: 'd', rank: 7 } },
    { type: 'pawn', color: 'black', square: { file: 'e', rank: 7 } },
    { type: 'pawn', color: 'black', square: { file: 'f', rank: 7 } },
    { type: 'pawn', color: 'black', square: { file: 'g', rank: 7 } },
    { type: 'pawn', color: 'black', square: { file: 'h', rank: 7 } },
    // Jesters
    { type: 'jester', color: 'white', square: { file: 'd', rank: 4 } },
    { type: 'jester', color: 'black', square: { file: 'e', rank: 5 } },
  ];

  const [pieces, setPieces] = useState<Piece[]>(initialPieces);

  const isSquareLight = (fileIndex: number, rankIndex: number): boolean => {
    return (fileIndex + rankIndex) % 2 === 0;
  };

  const getPieceAt = (square: Square, piecesList: Piece[]): Piece | undefined => {
    return piecesList.find(
      (p) => p.square.file === square.file && p.square.rank === square.rank
    );
  };

  // Check if path is clear (for rook, bishop, queen)
  const isPathClear = (from: Square, to: Square, piecesList: Piece[]): boolean => {
    const fromFileIndex = files.indexOf(from.file);
    const toFileIndex = files.indexOf(to.file);
    const fromRankIndex = ranks.indexOf(from.rank);
    const toRankIndex = ranks.indexOf(to.rank);

    const fileStep = toFileIndex > fromFileIndex ? 1 : toFileIndex < fromFileIndex ? -1 : 0;
    const rankStep = toRankIndex > fromRankIndex ? 1 : toRankIndex < fromRankIndex ? -1 : 0;

    let currentFileIndex = fromFileIndex + fileStep;
    let currentRankIndex = fromRankIndex + rankStep;

    while (currentFileIndex !== toFileIndex || currentRankIndex !== toRankIndex) {
      const checkSquare: Square = {
        file: files[currentFileIndex],
        rank: ranks[currentRankIndex] as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8
      };
      if (getPieceAt(checkSquare, piecesList)) {
        return false;
      }
      currentFileIndex += fileStep;
      currentRankIndex += rankStep;
    }
    return true;
  };

  // Get legal moves for a piece (with proper blocking checks)
  const getLegalMoves = (piece: Piece, piecesList: Piece[]): Square[] => {
    const legalMoves: Square[] = [];
    const currentSquare = piece.square;
    const fileIndex = files.indexOf(currentSquare.file);
    const rankIndex = ranks.indexOf(currentSquare.rank);

    if (piece.type === 'pawn') {
      const direction = piece.color === 'white' ? 1 : -1;
      const newRank = (currentSquare.rank + direction) as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
      if (newRank >= 1 && newRank <= 8) {
        const forwardSquare: Square = { file: currentSquare.file, rank: newRank };
        if (!getPieceAt(forwardSquare, piecesList)) {
          legalMoves.push(forwardSquare);
        }
      }
    } else if (piece.type === 'knight') {
      const knightMoves = [
        [-2, -1], [-2, 1], [-1, -2], [-1, 2],
        [1, -2], [1, 2], [2, -1], [2, 1]
      ];
      for (const [df, dr] of knightMoves) {
        const newFileIndex = fileIndex + df;
        const newRankIndex = rankIndex + dr;
        if (newFileIndex >= 0 && newFileIndex < 8 && newRankIndex >= 0 && newRankIndex < 8) {
          const newSquare: Square = { 
            file: files[newFileIndex], 
            rank: ranks[newRankIndex] as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 
          };
          const destPiece = getPieceAt(newSquare, piecesList);
          if (!destPiece || (destPiece.color !== piece.color && destPiece.type !== 'jester')) {
            legalMoves.push(newSquare);
          }
        }
      }
    } else if (piece.type === 'rook') {
      for (let i = 0; i < 8; i++) {
        if (i !== fileIndex) {
          const newSquare: Square = { file: files[i], rank: currentSquare.rank };
          if (isPathClear(currentSquare, newSquare, piecesList)) {
            const destPiece = getPieceAt(newSquare, piecesList);
            if (!destPiece || (destPiece.color !== piece.color && destPiece.type !== 'jester')) {
              legalMoves.push(newSquare);
            }
          }
        }
        if (i !== rankIndex) {
          const newSquare: Square = { file: currentSquare.file, rank: ranks[i] as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 };
          if (isPathClear(currentSquare, newSquare, piecesList)) {
            const destPiece = getPieceAt(newSquare, piecesList);
            if (!destPiece || (destPiece.color !== piece.color && destPiece.type !== 'jester')) {
              legalMoves.push(newSquare);
            }
          }
        }
      }
    } else if (piece.type === 'bishop') {
      for (let i = 1; i < 8; i++) {
        const diagonals = [
          [fileIndex + i, rankIndex + i],
          [fileIndex + i, rankIndex - i],
          [fileIndex - i, rankIndex + i],
          [fileIndex - i, rankIndex - i]
        ];
        for (const [nf, nr] of diagonals) {
          if (nf >= 0 && nf < 8 && nr >= 0 && nr < 8) {
            const newSquare: Square = { file: files[nf], rank: ranks[nr] as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 };
            if (isPathClear(currentSquare, newSquare, piecesList)) {
              const destPiece = getPieceAt(newSquare, piecesList);
              if (!destPiece || (destPiece.color !== piece.color && destPiece.type !== 'jester')) {
                legalMoves.push(newSquare);
              }
            }
          }
        }
      }
    } else if (piece.type === 'queen') {
      for (let i = 0; i < 8; i++) {
        if (i !== fileIndex) {
          const newSquare: Square = { file: files[i], rank: currentSquare.rank };
          if (isPathClear(currentSquare, newSquare, piecesList)) {
            const destPiece = getPieceAt(newSquare, piecesList);
            if (!destPiece || (destPiece.color !== piece.color && destPiece.type !== 'jester')) {
              legalMoves.push(newSquare);
            }
          }
        }
        if (i !== rankIndex) {
          const newSquare: Square = { file: currentSquare.file, rank: ranks[i] as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 };
          if (isPathClear(currentSquare, newSquare, piecesList)) {
            const destPiece = getPieceAt(newSquare, piecesList);
            if (!destPiece || (destPiece.color !== piece.color && destPiece.type !== 'jester')) {
              legalMoves.push(newSquare);
            }
          }
        }
      }
      for (let i = 1; i < 8; i++) {
        const diagonals = [
          [fileIndex + i, rankIndex + i],
          [fileIndex + i, rankIndex - i],
          [fileIndex - i, rankIndex + i],
          [fileIndex - i, rankIndex - i]
        ];
        for (const [nf, nr] of diagonals) {
          if (nf >= 0 && nf < 8 && nr >= 0 && nr < 8) {
            const newSquare: Square = { file: files[nf], rank: ranks[nr] as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 };
            if (isPathClear(currentSquare, newSquare, piecesList)) {
              const destPiece = getPieceAt(newSquare, piecesList);
              if (!destPiece || (destPiece.color !== piece.color && destPiece.type !== 'jester')) {
                legalMoves.push(newSquare);
              }
            }
          }
        }
      }
    }

    // Filter out moves to squares with jesters
    return legalMoves.filter(sq => {
      const pieceAtSquare = getPieceAt(sq, piecesList);
      return !pieceAtSquare || pieceAtSquare.type !== 'jester';
    });
  };

  // Animate moves: turn-based (white regular -> white dice -> white jester -> black regular -> black dice -> black jester)
  useEffect(() => {
    let currentPieces = [...initialPieces];
    let currentTurn: Color = 'white';
    let moveCount = 0;
    let stepInTurn = 0; // 0: regular piece, 1: dice roll, 2: jester move
    let currentDiceValues: { file: number; rank: number } | null = null; // Store dice values for jester move
    
    const runTurn = () => {
      if (stepInTurn === 0) {
        // Step 1: Current player's regular piece moves
        const regularPieces = currentPieces.filter(p => 
          p.type !== 'jester' && 
          p.type !== 'king' && 
          p.color === currentTurn
        );
        
        if (regularPieces.length === 0) {
          // No regular pieces, skip to jester
          stepInTurn = 1;
          runTurn();
          return;
        }

        // Find a piece with legal moves
        let piece: Piece | null = null;
        let legalMoves: Square[] = [];
        const shuffledPieces = [...regularPieces].sort(() => Math.random() - 0.5);
        
        for (const p of shuffledPieces) {
          const moves = getLegalMoves(p, currentPieces);
          if (moves.length > 0) {
            piece = p;
            legalMoves = moves;
            break;
          }
        }

        if (!piece || legalMoves.length === 0) {
          // No legal moves, skip to jester
          stepInTurn = 1;
          runTurn();
          return;
        }

        const fromSquare = piece.square;
        const toSquare = legalMoves[Math.floor(Math.random() * legalMoves.length)];

        // Remove captured piece if it's an opponent piece (not jester)
        const capturedPiece = getPieceAt(toSquare, currentPieces);
        
        setHighlightedSquare(toSquare);
        setAnimatedPiece({ piece, from: fromSquare, to: toSquare });

        setTimeout(() => {
          currentPieces = currentPieces
            .map(p => {
              if (p === piece) {
                return { ...p, square: toSquare };
              }
              // Remove captured opponent piece (but not jesters or kings)
              if (capturedPiece && p === capturedPiece && 
                  capturedPiece.type !== 'jester' && capturedPiece.type !== 'king' &&
                  capturedPiece.color !== piece.color) {
                return null;
              }
              return p;
            })
            .filter((p): p is Piece => p !== null);

          setPieces([...currentPieces]);
          setHighlightedSquare(null);
          setAnimatedPiece(null);
          stepInTurn = 1;
          runTurn();
        }, 1000);
      } else if (stepInTurn === 1) {
        // Step 2: Current player's dice roll
        const jester = currentPieces.find(p => p.type === 'jester' && p.color === currentTurn);
        if (!jester) {
          // No jester (was captured) - skip dice roll and jester move, switch turns
          currentTurn = currentTurn === 'white' ? 'black' : 'white';
          stepInTurn = 0;
          moveCount++;
          setTimeout(runTurn, 2000);
          return;
        }

        const jesterFromSquare = jester.square;
        let targetSquare: Square;
        let finalFile: number;
        let finalRank: number;

        // Determine target based on move count
        // According to rules: first die = file (1-8 = A-H), second die = rank (1-8 = ranks 1-8)
        if (moveCount < 10) {
          // Normal random move - roll dice to determine target
          do {
            finalFile = Math.floor(Math.random() * 8) + 1; // 1-8 for file A-H
            finalRank = Math.floor(Math.random() * 8) + 1; // 1-8 for rank 1-8
            const fileIndex = finalFile - 1; // Convert to 0-7 for files array
            targetSquare = { file: files[fileIndex], rank: finalRank as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 };
          } while (jesterFromSquare.file === targetSquare.file && jesterFromSquare.rank === targetSquare.rank);
          setAnimationPhase(0);
        } else if (moveCount === 10 && currentTurn === 'black') {
          // Black jester captures white king
          const whiteKing = currentPieces.find(p => p.type === 'king' && p.color === 'white');
          if (whiteKing) {
            targetSquare = whiteKing.square;
            // Convert square to dice values: file index + 1 = dice value, rank = dice value
            finalFile = files.indexOf(targetSquare.file) + 1; // A=1, B=2, ..., H=8
            finalRank = targetSquare.rank; // Already 1-8
            setAnimationPhase(1);
            setWinMessage('🎉 Black Jester Captures White King! Black Wins!');
          } else {
            do {
              finalFile = Math.floor(Math.random() * 8) + 1;
              finalRank = Math.floor(Math.random() * 8) + 1;
              const fileIndex = finalFile - 1;
              targetSquare = { file: files[fileIndex], rank: finalRank as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 };
            } while (jesterFromSquare.file === targetSquare.file && jesterFromSquare.rank === targetSquare.rank);
            setAnimationPhase(0);
          }
        } else if (moveCount === 11 && currentTurn === 'white') {
          // White jester lands on own king
          const whiteKing = currentPieces.find(p => p.type === 'king' && p.color === 'white');
          if (whiteKing) {
            targetSquare = whiteKing.square;
            // Convert square to dice values: file index + 1 = dice value, rank = dice value
            finalFile = files.indexOf(targetSquare.file) + 1; // A=1, B=2, ..., H=8
            finalRank = targetSquare.rank; // Already 1-8
            setAnimationPhase(2);
            setWinMessage('💥 White Jester Lands on Own King! Black Wins!');
          } else {
            // Reset
            moveCount = 0;
            currentTurn = 'white';
            currentPieces = [...initialPieces];
            setPieces([...initialPieces]);
            stepInTurn = 0;
            setTimeout(runTurn, 2000);
            return;
          }
        } else {
          // Normal random move
          do {
            finalFile = Math.floor(Math.random() * 8) + 1;
            finalRank = Math.floor(Math.random() * 8) + 1;
            const fileIndex = finalFile - 1;
            targetSquare = { file: files[fileIndex], rank: finalRank as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 };
          } while (jesterFromSquare.file === targetSquare.file && jesterFromSquare.rank === targetSquare.rank);
          setAnimationPhase(0);
        }

        // Show dice and roll
        setShowDice(true);
        setDiceRolling(true);

        const diceInterval = setInterval(() => {
          setDiceValues({
            file: Math.floor(Math.random() * 8) + 1,
            rank: Math.floor(Math.random() * 8) + 1,
          });
        }, 250);

        setTimeout(() => {
          clearInterval(diceInterval);
          // Store dice values for jester move (use closure variable, not state)
          currentDiceValues = { file: finalFile, rank: finalRank };
          setDiceValues({ file: finalFile, rank: finalRank }); // Update UI
          setDiceRolling(false);
          stepInTurn = 2;
          runTurn();
        }, 2500);
      } else if (stepInTurn === 2) {
        // Step 3: Current player's jester moves
        const jester = currentPieces.find(p => p.type === 'jester' && p.color === currentTurn);
        if (!jester) {
          // No jester, switch turns
          currentTurn = currentTurn === 'white' ? 'black' : 'white';
          stepInTurn = 0;
          moveCount++;
          setTimeout(runTurn, 2000);
          return;
        }

        if (!currentDiceValues) {
          // No dice values, skip
          currentTurn = currentTurn === 'white' ? 'black' : 'white';
          stepInTurn = 0;
          moveCount++;
          setTimeout(runTurn, 2000);
          return;
        }

        const jesterFromSquare = jester.square;
        // Get target from dice values (dice 1-8 maps to file A-H and rank 1-8)
        // Dice value 1 = file A / rank 1, dice value 2 = file B / rank 2, etc.
        // Per rules: first die = file (1-8 = A-H), second die = rank (1-8 = ranks 1-8)
        const fileIndex = currentDiceValues.file - 1; // 0-7 for files array (dice 1 = A, dice 2 = B, etc.)
        const targetSquare: Square = { 
          file: files[fileIndex], 
          rank: currentDiceValues.rank as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 
        };

        // Check if jester lands on a piece (capture)
        const targetPiece = getPieceAt(targetSquare, currentPieces);
        const isKingCapture = targetPiece && targetPiece.type === 'king';
        const isJesterCapture = targetPiece && targetPiece.type === 'jester' && targetPiece.color !== currentTurn;

        setHighlightedSquare(targetSquare);
        setAnimatedPiece({ piece: jester, from: jesterFromSquare, to: targetSquare });

        setTimeout(() => {
          if (isKingCapture && targetPiece) {
            // King captured - determine winner based on whose king
            let winnerColor: Color;
            let loserColor: Color;
            
            if (targetPiece.color === currentTurn) {
              // Jester landed on OWN king - instant LOSS for current player
              // Opponent wins
              winnerColor = currentTurn === 'white' ? 'black' : 'white';
              loserColor = currentTurn;
              setWinMessage(`💥 ${loserColor === 'white' ? 'White' : 'Black'} Jester Lands on Own King! ${winnerColor === 'white' ? 'White' : 'Black'} Wins!`);
            } else {
              // Jester landed on OPPONENT's king - instant WIN for current player
              winnerColor = currentTurn;
              loserColor = targetPiece.color;
              setWinMessage(`🎉 ${winnerColor === 'white' ? 'White' : 'Black'} Jester Captures ${loserColor === 'white' ? 'White' : 'Black'} King! ${winnerColor === 'white' ? 'White' : 'Black'} Wins!`);
            }
            
            setGameWon(true);
            
            currentPieces = currentPieces
              .map(p => {
                if (p.type === 'jester' && p.color === currentTurn) {
                  return { ...p, square: targetSquare };
                }
                if (targetPiece && p === targetPiece && p.type === 'king') {
                  return null; // Remove captured king
                }
                return p;
              })
              .filter((p): p is Piece => p !== null);

            setPieces([...currentPieces]);
            
            // Show win for longer, then reset
            setTimeout(() => {
              setGameWon(false);
              setWinMessage('');
              moveCount = 0;
              currentTurn = 'white';
              currentPieces = [...initialPieces];
              setPieces([...initialPieces]);
              setHighlightedSquare(null);
              setAnimatedPiece(null);
              stepInTurn = 0;
              setTimeout(runTurn, 2000);
            }, 4000);
          } else if (isJesterCapture) {
            // Jester captured by opposing jester - remove captured jester
            currentPieces = currentPieces
              .map(p => {
                if (p.type === 'jester' && p.color === currentTurn) {
                  return { ...p, square: targetSquare };
                }
                // Remove captured jester
                if (targetPiece && p === targetPiece && p.type === 'jester') {
                  return null;
                }
                return p;
              })
              .filter((p): p is Piece => p !== null);

            setPieces([...currentPieces]);
            setHighlightedSquare(null);
            setAnimatedPiece(null);
            
            // Switch turns after jester move completes
            currentTurn = currentTurn === 'white' ? 'black' : 'white';
            stepInTurn = 0;
            moveCount++;
            setTimeout(runTurn, 2000);
          } else {
            // Normal jester move - capture piece if any (but not jesters or kings)
            currentPieces = currentPieces
              .map(p => {
                if (p.type === 'jester' && p.color === currentTurn) {
                  return { ...p, square: targetSquare };
                }
                // Remove captured piece (but not jesters or kings)
                if (targetPiece && p === targetPiece && 
                    targetPiece.type !== 'jester' && targetPiece.type !== 'king') {
                  return null;
                }
                return p;
              })
              .filter((p): p is Piece => p !== null);

            setPieces([...currentPieces]);
            setHighlightedSquare(null);
            setAnimatedPiece(null);
            
            // Switch turns after jester move completes
            currentTurn = currentTurn === 'white' ? 'black' : 'white';
            stepInTurn = 0;
            moveCount++;
            setTimeout(runTurn, 2000);
          }
        }, 1000);
      }
    };

    // Start the animation
    const timeout = setTimeout(runTurn, 1000);
    return () => clearTimeout(timeout);
  }, []);

  const squareSize = size / 8;

  return (
    <div className="animated-board-wrapper">
      {/* Win message */}
      {gameWon && winMessage && (
        <div className="animated-win-message">
          <h3>{winMessage}</h3>
        </div>
      )}
      {/* Dice display - always visible */}
      {showDice && (
        <div className="animated-dice-container">
          <div className="animated-dice-wrapper">
            <div className={`animated-die file-die ${diceRolling ? 'rolling' : ''}`}>
              <div className="animated-die-value">
                <span className="animated-die-number">{diceValues.file}</span>
                <span className="animated-die-label">{files[diceValues.file - 1]}</span>
              </div>
            </div>
            <div className={`animated-die rank-die ${diceRolling ? 'rolling' : ''}`}>
              <div className="animated-die-value">
                <span className="animated-die-number">{diceValues.rank}</span>
                <span className="animated-die-label">Rank</span>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="animated-board-container" style={{ width: size, height: size }}>
        <div className="animated-board">
        {ranks.map((rank, rankIndex) => (
          <div key={rank} className="animated-rank">
            {files.map((file, fileIndex) => {
              const square: Square = { file, rank: rank as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 };
              // Hide piece at source during animation
              const isAnimatedSource = animatedPiece && 
                animatedPiece.from.file === square.file && 
                animatedPiece.from.rank === square.rank;
              const piece = isAnimatedSource ? null : getPieceAt(square, pieces);
              const isLight = isSquareLight(fileIndex, rankIndex);
              const isHighlighted = highlightedSquare && 
                highlightedSquare.file === square.file && 
                highlightedSquare.rank === square.rank;

              return (
                <div
                  key={`${file}${rank}`}
                  className={`animated-square ${isLight ? 'light' : 'dark'} ${isHighlighted ? 'highlighted' : ''}`}
                  style={{ width: squareSize, height: squareSize }}
                >
                  {piece && <PieceIcon piece={piece} />}
                </div>
              );
            })}
          </div>
        ))}
        {/* Animated piece overlay */}
        {animatedPiece && (() => {
          const fromX = files.indexOf(animatedPiece.from.file) * squareSize;
          const fromY = (8 - animatedPiece.from.rank) * squareSize;
          const toX = files.indexOf(animatedPiece.to.file) * squareSize;
          const toY = (8 - animatedPiece.to.rank) * squareSize;
          return (
            <div
              className="animated-piece-overlay"
              style={{
                left: `${fromX}px`,
                top: `${fromY}px`,
                width: squareSize,
                height: squareSize,
                '--delta-x': `${toX - fromX}px`,
                '--delta-y': `${toY - fromY}px`,
              } as React.CSSProperties & { '--delta-x': string; '--delta-y': string }}
            >
              <PieceIcon piece={animatedPiece.piece} />
            </div>
          );
        })()}
        </div>
      </div>
    </div>
  );
};
